import { useParams, Link } from 'react-router-dom';
import React, { useMemo } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { catalog, catalogAdmin, type AudioRecheckResult } from '../api/index.js';
import { useEnableEdit } from '../hooks/useAuth.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import { Loader2, AlertTriangle, Pencil, AlertCircle, ChevronDown, CheckCircle2, XCircle, Info, RefreshCw } from 'lucide-react';
import { labelForItemFormat, labelForItemType } from '../lib/itemTaxonomy.js';
import { historySlugsFromIglu } from '../lib/iglu-history-slugs.js';
import { CAST_ROLE_OPTIONS } from '../lib/castRoles.js';

const ROLE_LABEL_MAP = new Map(CAST_ROLE_OPTIONS.map(r => [r.value, r.label]));
function humanizeRole(role: string): string {
  return ROLE_LABEL_MAP.get(role) ?? role;
}

const SENTINEL = 'ig.field.type.delete';

function unwrapOid(v: unknown): unknown {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    if (typeof o.$oid === 'string') return o.$oid;
    if (typeof o._id === 'string') return o._id;
  }
  return v;
}

function igluVal(v: unknown): string | undefined {
  const u = unwrapOid(v);
  if (u === undefined || u === null || u === '' || u === SENTINEL) return undefined;
  return String(u);
}

// Image may be a URL string or an object with a url/src/href property
function igluImageUrl(v: unknown): string | undefined {
  if (!v || v === SENTINEL) return undefined;
  if (typeof v === 'string') return v || undefined;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.url === 'string') return o.url || undefined;
    if (typeof o.src === 'string') return o.src || undefined;
    if (typeof o.href === 'string') return o.href || undefined;
  }
  return undefined;
}

function igluDate(v: unknown): Date | undefined {
  const s = igluVal(v);
  if (!s) return undefined;
  const d = new Date(s);
  return isNaN(d.getTime()) ? undefined : d;
}

function igluNum(v: unknown): number | undefined {
  if (v === undefined || v === null || v === SENTINEL) return undefined;
  const n = Number(v);
  return isNaN(n) ? undefined : n;
}

function igluArr(v: unknown): string[] | undefined {
  if (!Array.isArray(v) || v.length === 0) return undefined;
  const filtered = v
    .map(unwrapOid)
    .filter((x) => x !== SENTINEL && x !== null && x !== undefined);
  return filtered.length > 0 ? filtered.map(String) : undefined;
}

// Strip RTF markup — detects {\rtf prefix and removes control words/braces
function stripRtf(s: string): string {
  if (!s || !s.trim().startsWith('{\\rtf')) return s;
  return s
    .replace(/\{\\[^{}]*\}/g, '')
    .replace(/\\[a-z]+\d*[ ]?/gi, '')
    .replace(/\\\\/g, '\\')
    .replace(/\\[{}]/g, '')
    .replace(/[{}]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

class ItemErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: string | null }
> {
  constructor(props: any) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e: Error) { return { error: e.message }; }
  render() {
    if (this.state.error) return (
      <div className="p-8 text-red-600">
        <p className="font-bold">Render error</p>
        <pre className="text-xs mt-2 whitespace-pre-wrap">{this.state.error}</pre>
      </div>
    );
    return this.props.children;
  }
}

const AT_RISK_MSGS: Record<string, { msg: string; color: string }> = {
  release_imminent_draft: { msg: 'Release imminent — still in draft. Publish immediately.', color: 'bg-red-50 border-red-300 text-red-800' },
  releasing_soon_draft: { msg: 'Releasing within 30 days but still draft.', color: 'bg-yellow-50 border-yellow-300 text-yellow-800' },
  missing_publisher: { msg: 'No internal (backend) publisher assigned.', color: 'bg-orange-50 border-orange-300 text-orange-800' },
  publisher_backend_mismatch: { msg: "Display publisher doesn't match internal (backend) publisher.", color: 'bg-purple-50 border-purple-300 text-purple-800' },
  missing_description: { msg: 'Missing description.', color: 'bg-orange-50 border-orange-300 text-orange-800' },
  missing_genre: { msg: 'No genres assigned.', color: 'bg-orange-50 border-orange-300 text-orange-800' },
  no_contributors: { msg: 'No contributors (cast/crew) assigned.', color: 'bg-orange-50 border-orange-300 text-orange-800' },
  missing_image: { msg: 'No cover image.', color: 'bg-orange-50 border-orange-300 text-orange-800' },
  missing_audio: { msg: 'Missing audio files — check item resources.', color: 'bg-orange-50 border-orange-300 text-orange-800' },
  partial_audio: { msg: 'Some chapters/resources are missing a quality tier (96k or 192k) — most audio plays fine, but a few files need re-uploading.', color: 'bg-yellow-50 border-yellow-300 text-yellow-800' },
  missing_sample: { msg: 'No sample uploaded.', color: 'bg-orange-50 border-orange-300 text-orange-800' },
  publisher_series_mismatch: { msg: "Publisher doesn't match the series publisher.", color: 'bg-purple-50 border-purple-300 text-purple-800' },
  format_mismatch: { msg: "Format is missing or doesn't match the parent series/group.", color: 'bg-pink-50 border-pink-300 text-pink-800' },
  group_formats_empty: { msg: 'Parent group has no formats. Series may be hidden in the app.', color: 'bg-pink-50 border-pink-300 text-pink-800' },
};

interface DiagCheck {
  id: string;
  label: string;
  status: 'blocked' | 'warning' | 'ok' | 'info';
  detail: string;
}

const DIAG_STATUS_STYLE: Record<DiagCheck['status'], { icon: typeof CheckCircle2; className: string }> = {
  blocked: { icon: XCircle, className: 'bg-red-50 border-red-200 text-red-800' },
  warning: { icon: AlertTriangle, className: 'bg-orange-50 border-orange-200 text-orange-800' },
  ok: { icon: CheckCircle2, className: 'bg-green-50 border-green-200 text-green-800' },
  info: { icon: Info, className: 'bg-blue-50 border-blue-200 text-blue-800' },
};

interface RoleVisibility {
  role: string;
  label: string;
  visible: boolean;
  blockingReasons: string[];
}

/** Always-visible per-viewer-role status: external user / internal draft-preview permission. */
function RoleVisibilityRow({ roles }: { roles: RoleVisibility[] }) {
  if (!roles.length) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-6 pb-4">
      {roles.map((r) => (
        <div
          key={r.role}
          className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-sm ${
            r.visible ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {r.visible ? (
            <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
          ) : (
            <XCircle size={16} className="flex-shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <p className="font-medium">
              {r.label} - {r.visible ? 'Visible' : 'Not visible'}
            </p>
            {!r.visible && r.blockingReasons.length > 0 && (
              <p className="text-xs opacity-90 mt-0.5 truncate" title={r.blockingReasons.join('; ')}>
                {r.blockingReasons.join('; ')}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/** "Why isn't this showing?" checklist — best-effort, not a guarantee of consumer-app behavior. */
function VisibilityDiagnosticsPanel({ checks, roles }: { checks: DiagCheck[]; roles: RoleVisibility[] }) {
  const [open, setOpen] = React.useState(false);
  if (!checks.length && !roles.length) return null;
  const blockedCount = checks.filter((c) => c.status === 'blocked').length;
  const warningCount = checks.filter((c) => c.status === 'warning').length;
  const shownChecks = checks.filter((c) => c.status !== 'ok');
  const passedChecks = checks.filter((c) => c.status === 'ok');

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <CardTitle>Why isn&apos;t this showing?</CardTitle>
          {blockedCount > 0 && <Badge variant="error">{blockedCount} blocking</Badge>}
          {warningCount > 0 && <Badge variant="warning">{warningCount} warning{warningCount !== 1 ? 's' : ''}</Badge>}
          {blockedCount === 0 && warningCount === 0 && <Badge variant="success">All clear</Badge>}
        </div>
        <ChevronDown size={18} className={`text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      <RoleVisibilityRow roles={roles} />
      {open && (
        <CardContent className="pt-0 space-y-2">
          {shownChecks.length > 0 && (
            <div className="space-y-1.5">
              {shownChecks.map((c) => {
                const style = DIAG_STATUS_STYLE[c.status] ?? DIAG_STATUS_STYLE.info;
                const Icon = style.icon;
                return (
                  <div key={c.id} className={`flex items-start gap-2 px-3 py-1.5 rounded-lg border text-sm ${style.className}`}>
                    <Icon size={14} className="flex-shrink-0 mt-0.5" />
                    <p className="min-w-0"><span className="font-medium">{c.label}:</span> {c.detail}</p>
                  </div>
                );
              })}
            </div>
          )}
          {passedChecks.length > 0 && (
            <div className={`flex flex-wrap gap-x-3 gap-y-1 ${shownChecks.length > 0 ? 'pt-1 border-t border-gray-100' : ''}`}>
              {passedChecks.map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1 text-xs text-gray-500">
                  <CheckCircle2 size={12} className="text-green-500 flex-shrink-0" />
                  {c.label}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

/** Live result of a one-off "Recheck audio" — bypasses the mirror, reads Iglu resources directly. */
function AudioRecheckResultBanner({ result }: { result: AudioRecheckResult }) {
  const ok = result.has96kAudio && result.has192kAudio;
  if (ok) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-lg border text-sm bg-green-50 border-green-300 text-green-800">
        <CheckCircle2 size={16} className="flex-shrink-0" />
        Verified live from Iglu: all {result.resourceCount} audio files have both 96k and 192k. Mirror updated.
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 px-4 py-3 rounded-lg border text-sm bg-orange-50 border-orange-300 text-orange-800">
      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
      <div>
        <p>Verified live from Iglu: of {result.resourceCount} audio files, {result.missingLowRes.length} missing 96k, {result.missingHighRes.length} missing 192k.</p>
        {result.missingLowRes.length > 0 && (
          <p className="text-xs opacity-90 mt-1">Missing 96k: {result.missingLowRes.join(', ')}</p>
        )}
        {result.missingHighRes.length > 0 && (
          <p className="text-xs opacity-90 mt-1">Missing 192k: {result.missingHighRes.join(', ')}</p>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value?: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex gap-4 py-1.5 text-sm border-b border-gray-50 last:border-0">
      <span className="w-44 text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-gray-900 break-words min-w-0">{value}</span>
    </div>
  );
}

function BoolRow({ label, value }: { label: string; value?: boolean }) {
  if (value === undefined || value === null) return null;
  return <Row label={label} value={value ? 'Yes' : 'No'} />;
}

export function ItemDetail() {
  const { id } = useParams<{ id: string }>();
  const canEdit = useEnableEdit();
  const qc = useQueryClient();

  const { data: item, isLoading, error } = useQuery({
    queryKey: ['catalog-item', id],
    queryFn: () => catalog.item(id!),
    enabled: !!id,
  });

  const recheckAudio = useMutation({
    mutationFn: () => catalog.recheckAudio(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog-items'] }),
  });

  const { data: publishersData } = useQuery({
    queryKey: ['catalog-publishers'],
    queryFn: catalog.publishers,
    staleTime: 10 * 60 * 1000,
    enabled: !!item,
  });

  const { data: genresData } = useQuery({
    queryKey: ['catalog-genres'],
    queryFn: catalog.genres,
    staleTime: 10 * 60 * 1000,
    enabled: !!item,
  });

  const { data: imprintsData } = useQuery({
    queryKey: ['catalog-imprints'],
    queryFn: catalog.imprints,
    staleTime: 10 * 60 * 1000,
    enabled: !!item,
  });

  const { data: seriesData } = useQuery({
    queryKey: ['catalog-series'],
    queryFn: catalog.series,
    staleTime: 10 * 60 * 1000,
    enabled: !!item,
  });

  const groupId = item ? igluVal(item.groupId) : undefined;
  const { data: groupData } = useQuery({
    queryKey: ['group-detail', groupId],
    queryFn: () => catalogAdmin.groupDetail(groupId!),
    staleTime: 10 * 60 * 1000,
    enabled: !!groupId,
  });

  const contributorTmIds = useMemo(() => {
    const raw = item?.cast;
    if (!Array.isArray(raw)) return [];
    const ids = new Set<string>();
    for (const c of raw as Array<Record<string, unknown>>) {
      const rawId = c.teamMemberId ?? c.teamMember;
      const tmId = String(unwrapOid(rawId) ?? '');
      if (tmId && tmId !== SENTINEL) ids.add(tmId);
    }
    return [...ids];
  }, [item]);

  const contributorDetailQueries = useQueries({
    queries: contributorTmIds.map((tid) => ({
      queryKey: ['catalog-contributor-detail', tid],
      queryFn: () => catalogAdmin.contributorDetail(tid),
      enabled: contributorTmIds.length > 0 && !!item,
      staleTime: 10 * 60 * 1000,
    })),
  });

  const contributorMapFromDetail = useMemo(() => {
    const m = new Map<string, string>();
    contributorTmIds.forEach((tid, i) => {
      const d = contributorDetailQueries[i]?.data as { name?: string } | undefined;
      m.set(tid, typeof d?.name === 'string' ? d.name : tid);
    });
    return m;
  }, [contributorTmIds, contributorDetailQueries]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error) {
    const isNotFound = error instanceof Error && error.message.includes('404');
    return (
      <div className="p-8 flex items-center gap-3 text-red-600">
        <AlertCircle size={20} />
        <div>
          <p className="font-semibold">{isNotFound ? 'Item not found' : 'Failed to load item'}</p>
          {!isNotFound && <p className="text-sm text-red-400 mt-1">{error.message}</p>}
        </div>
      </div>
    );
  }

  if (!item) return null;

  const publisherMap = new Map((publishersData?.publishers ?? []).map(p => [p._id, p.value]));
  const genreMap = new Map((genresData?.genres ?? []).map(g => [g._id, g.name]));
  const imprintMap = new Map((imprintsData?.imprints ?? []).map(p => [p._id, p.value]));
  const contributorMap = contributorMapFromDetail;
  const seriesMap = new Map((seriesData?.series ?? []).map(s => [s._id, s.name]));

  const flags = item.atRiskFlags as string[] ?? [];
  const history = item.importHistory as Array<{ id: string; batchId: string; diffAction: string; createdAt: string }> ?? [];

  const releaseDate = igluDate(item.releaseAt);
  const originalReleaseDate = igluDate(item.originalReleaseAt);
  const preorderDate = igluDate(item.preorderReleaseAt);
  const price = igluNum(item.price);
  const duration = igluNum(item.totalDuration);
  const sampleUrl = igluVal(item.sampleUrl);
  const imageUrl = igluImageUrl(item.image);

  const publisherIds = igluArr(item.publishers);
  const genreIds = igluArr(item.genres);
  const seriesId = igluVal(item.seriesId);

  const publisherLinks = publisherIds?.map(pid => {
    const name = publisherMap.get(pid) ?? pid;
    return <Link key={pid} to={`/publishers/${pid}`} className="text-brand-600 hover:underline mr-2">{name}</Link>;
  });
  const genreLinks = genreIds?.map(gid => {
    const name = genreMap.get(gid) ?? gid;
    return <Link key={gid} to={`/genres/${gid}`} className="text-brand-600 hover:underline mr-2">{name}</Link>;
  });
  const imprintIds = igluArr(item.imprints) ?? [];
  const imprintNames = imprintIds.map(iid => imprintMap.get(iid) ?? iid).join(', ');
  const seriesName = seriesId ? (seriesMap.get(seriesId) ?? seriesId) : undefined;
  const groupName = groupId ? (groupData?.name ?? groupId) : undefined;

  const castEntries = Array.isArray(item.cast)
    ? (item.cast as Array<Record<string, unknown>>)
        .map(c => {
          // Iglu may use either 'teamMemberId' or 'teamMember'
          const rawId = c.teamMemberId ?? c.teamMember;
          const tmId = String(unwrapOid(rawId) ?? '');
          const name = contributorMap.get(tmId) ?? (tmId || null);
          const roles = Array.isArray(c.roles)
            ? (c.roles as string[]).map(humanizeRole).join(', ')
            : humanizeRole(String(c.roles ?? c.type ?? ''));
          return { name, roles, tmId };
        })
        .filter(c => c.tmId && c.tmId !== SENTINEL)
    : [];

  const webSynopsis = igluVal(item.webSynopsis);
  const webSynopsisClean = webSynopsis ? stripRtf(webSynopsis) : undefined;
  const slugHistory = historySlugsFromIglu(item.historySlugs);

  return (
    <ItemErrorBoundary>
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          {imageUrl && (
            <img
              src={imageUrl}
              alt="Cover"
              className="w-20 h-20 rounded-lg object-cover bg-gray-100 border border-gray-200 flex-shrink-0 mt-6"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          <div className="min-w-0">
            <Link to="/catalog" className="text-sm text-brand-600 hover:underline mb-2 inline-block">← Back to Catalog Items</Link>
            <h1 className="text-2xl font-bold text-gray-900">{igluVal(item.name) ?? '(no title)'}</h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge variant={statusBadge(igluVal(item.status) ?? '')}>{igluVal(item.status) ?? '—'}</Badge>
              {releaseDate && (
                <span className="text-sm text-gray-500">
                  Release: {format(releaseDate, 'MMM d, yyyy')}
                </span>
              )}
            </div>
          </div>
        </div>
        {canEdit && (
          <Link
            to={`/catalog/${item._id}/edit`}
            className="flex items-center gap-1.5 text-sm text-brand-600 border border-brand-200 rounded-lg px-3 py-2 hover:bg-brand-50 transition-colors flex-shrink-0"
          >
            <Pencil size={14} /> Edit
          </Link>
        )}
      </div>

      {/* At-risk banners */}
      {flags.length > 0 && (
        <div className="space-y-2">
          {flags.map((flag) => {
            const info = AT_RISK_MSGS[flag];
            if (!info) return null;
            return (
              <div key={flag} className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm ${info.color}`}>
                <AlertTriangle size={16} className="flex-shrink-0" />
                <span className="flex-1">{info.msg}</span>
                {(flag === 'missing_audio' || flag === 'partial_audio') && (
                  <button
                    type="button"
                    onClick={() => recheckAudio.mutate()}
                    disabled={recheckAudio.isPending}
                    className="flex items-center gap-1.5 text-xs font-medium border border-current rounded-lg px-2.5 py-1 hover:bg-white/50 transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    {recheckAudio.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    Recheck audio
                  </button>
                )}
              </div>
            );
          })}
          {recheckAudio.data && <AudioRecheckResultBanner result={recheckAudio.data} />}
          {recheckAudio.isError && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg border text-sm bg-red-50 border-red-300 text-red-800">
              <AlertCircle size={16} className="flex-shrink-0" />
              Recheck failed: {(recheckAudio.error as Error)?.message ?? 'unknown error'}
            </div>
          )}
        </div>
      )}

      {/* Why isn't this showing? */}
      <VisibilityDiagnosticsPanel
        checks={(item.visibilityDiagnostics as DiagCheck[]) ?? []}
        roles={(item.roleVisibility as RoleVisibility[]) ?? []}
      />

      {/* Identifiers */}
      <Section title="Identifiers">
        <Row label="ISBN-13" value={igluVal(item.isbn13)} />
        <Row label="Book ID" value={igluVal(item.bookId)} />
        <Row label="Slug" value={igluVal(item.slug) ? <span className="font-mono text-xs">{igluVal(item.slug)}</span> : undefined} />
        {slugHistory.length > 0 && (
          <div className="flex gap-4 py-1.5 text-sm border-b border-gray-50">
            <span className="w-44 text-gray-500 flex-shrink-0">Previous slugs</span>
            <ul className="flex flex-wrap gap-1.5 min-w-0 list-none m-0 p-0">
              {slugHistory.map((s) => (
                <li
                  key={s}
                  className="font-mono text-xs bg-gray-50 border border-gray-200 rounded px-2 py-0.5 text-gray-800"
                >
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Row label="Admin ID" value={<span className="font-mono text-xs">{item._id}</span>} />
      </Section>

      {/* Description */}
      {(igluVal(item.description) || igluVal(item.shortDescription) || webSynopsisClean) && (
        <Section title="Description">
          {igluVal(item.shortDescription) && <p className="text-sm text-gray-600 italic mb-3">{igluVal(item.shortDescription)}</p>}
          {igluVal(item.description) && <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">{igluVal(item.description)}</p>}
          {webSynopsisClean && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Web Synopsis</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{webSynopsisClean}</p>
            </div>
          )}
        </Section>
      )}

      {/* Classification */}
      <Section title="Classification">
        <Row label="Language" value={igluVal(item.language)} />
        <Row label="Edition" value={igluVal(item.edition)} />
        <Row label="Format" value={labelForItemFormat(igluVal(item.format))} />
        <Row label="Type" value={labelForItemType(igluVal(item.type))} />
        <Row label="BISAC" value={igluArr(item.bisac)?.join(', ')} />
        <Row label="Keywords" value={igluArr(item.keywords)?.join(', ')} />
        <Row label="Tags" value={igluArr(item.tags)?.join(', ')} />
        <Row label="Country Codes" value={igluArr(item.countryCodes)?.join(', ')} />
        <Row label="Imprints" value={imprintNames || undefined} />
      </Section>

      {/* Series & Grouping */}
      <Section title="Series & Grouping">
        <Row label="Series" value={seriesName ? (
          <Link to={`/series/${seriesId}`} className="text-brand-600 hover:underline">
            {seriesName}
          </Link>
        ) : undefined} />
        <Row label="Group" value={groupName ? (
          <Link to={`/groups/${groupId}`} className="text-brand-600 hover:underline">{groupName}</Link>
        ) : undefined} />
      </Section>

      {/* Publishers & Genres */}
      <Section title="Publishers & Genres">
        <Row label="Publishers" value={publisherLinks && publisherLinks.length > 0 ? <>{publisherLinks}</> : undefined} />
        <Row label="Genres" value={genreLinks && genreLinks.length > 0 ? <>{genreLinks}</> : undefined} />
      </Section>

      {/* Cast & Crew */}
      {castEntries.length > 0 && (
        <Section title="Cast & Crew">
          <div className="space-y-2">
            {castEntries.map((entry, i) => (
              <div key={i} className="flex items-center gap-4 py-1.5 text-sm border-b border-gray-50 last:border-0">
                <span className="text-gray-900 w-48 flex-shrink-0">
                  <Link to={`/contributors/${entry.tmId}`} className="text-brand-600 hover:underline">
                    {entry.name}
                  </Link>
                </span>
                <span className="text-gray-500 text-xs">{entry.roles}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Dates */}
      <Section title="Dates">
        <Row label="Release" value={releaseDate ? format(releaseDate, 'MMM d, yyyy HH:mm') : undefined} />
        <Row label="Original Release" value={originalReleaseDate ? format(originalReleaseDate, 'MMM d, yyyy') : undefined} />
        <Row label="Preorder" value={preorderDate ? format(preorderDate, 'MMM d, yyyy') : undefined} />
        <BoolRow label="Show release date on preorder" value={typeof item.showReleaseDateOnPreorder === 'boolean' ? item.showReleaseDateOnPreorder : undefined} />
      </Section>

      {/* Pricing */}
      <Section title="Pricing">
        <Row label="Price" value={price !== undefined ? `$${price.toFixed(2)}` : undefined} />
        <BoolRow label="Free" value={typeof item.free === 'boolean' ? item.free : undefined} />
        <BoolRow label="Purchasable" value={typeof item.purchasable === 'boolean' ? item.purchasable : undefined} />
        <BoolRow label="Featured on app" value={typeof item.featuredOnApp === 'boolean' ? item.featuredOnApp : undefined} />
      </Section>

      {/* Media */}
      <Section title="Media">
        {imageUrl && (
          <div className="mb-4">
            <img
              src={imageUrl}
              alt="Cover"
              className="w-32 h-32 rounded-lg object-cover bg-gray-100 border border-gray-200"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        )}
        <Row label="Sample URL" value={sampleUrl ? <a href={sampleUrl} target="_blank" rel="noopener noreferrer" className="text-brand-600 underline break-all">{sampleUrl}</a> : undefined} />
        <Row label="Duration" value={duration ? `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m` : undefined} />
      </Section>

      {/* Status flags */}
      <Section title="Status Flags">
        <BoolRow label="Archived" value={typeof item.isArchived === 'boolean' ? item.isArchived : undefined} />
        {item.parentalControlOptions != null && typeof item.parentalControlOptions === 'object' && (item.parentalControlOptions as Record<string, unknown>).contentRatingReviewed !== undefined && (
          <BoolRow label="Content rating reviewed" value={Boolean((item.parentalControlOptions as Record<string, unknown>).contentRatingReviewed)} />
        )}
      </Section>

      {/* Reviews & ratings */}
      {[item.averageRating, item.averagePerformance, item.averagePlot, item.averageSpecialEffects, item.totalReviews].some(v => v && v !== 'ig.field.type.delete') && (
        <Section title="Reviews">
          <Row label="Average Rating" value={igluVal(item.averageRating)} />
          <Row label="Avg Performance" value={igluVal(item.averagePerformance)} />
          <Row label="Avg Plot" value={igluVal(item.averagePlot)} />
          <Row label="Avg Special Effects" value={igluVal(item.averageSpecialEffects)} />
          <Row label="Total Reviews" value={igluVal(item.totalReviews)} />
        </Section>
      )}

      {/* Metadata */}
      <Section title="Metadata">
        <Row label="Created" value={igluDate(item.createdAt) ? format(igluDate(item.createdAt)!, 'MMM d, yyyy HH:mm') : undefined} />
        <Row label="Updated" value={igluDate(item.updatedAt) ? format(igluDate(item.updatedAt)!, 'MMM d, yyyy HH:mm') : undefined} />
      </Section>

      {/* Import history */}
      {history.length > 0 && (
        <Section title="Import History">
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <Link to={`/batches/${h.batchId}`} className="text-sm text-brand-600 hover:underline font-mono">{h.batchId}</Link>
                  <p className="text-xs text-gray-400">{formatDistanceToNow(new Date(h.createdAt), { addSuffix: true })}</p>
                </div>
                <Badge variant={statusBadge(h.diffAction)}>{h.diffAction}</Badge>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
    </ItemErrorBoundary>
  );
}
